import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DoubleArrow } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useLessonsQueryApi, useSchoolFreeDaysQueryApi } from 'api';
import { LoadingWrapper, WeeklySchedule } from 'components/custom';
import { convertLessonsToSlots } from 'components/custom/WeeklySchedule';
import { useIsUntisUser } from 'components/kratos/hooks/useIsUntisUser';
import { addWeeks, endOfDay, format, isEqual, isWeekend, nextFriday, nextMonday, startOfWeek, } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeLessonsIntoBlocks } from 'utils/untis-blocks';
const AUTO_REFRESH_INTERVAL_MS = 2000;
const AUTO_REFRESH_TIMEOUT_MS = 30000;
let persistedInterval = null;
const CalendarForDashboard = ({ startDate, autoRefresh = false, }) => {
    const { t } = useTranslation('timetable', { keyPrefix: 'weeklyCalendar' });
    /*
     * On weekends, jump to next Monday so the calendar shows next week's
     * lessons — consistent with the Lessons widget behaviour and the
     * Cypress createLesson helper (effectiveToday).
     */
    const today = new Date();
    const now = startDate ?? (isWeekend(today) ? nextMonday(today) : today);
    const monday = startOfWeek(now, { weekStartsOn: 1 });
    const friday = endOfDay(nextFriday(monday));
    const intervalForCurrentWeek = {
        end: friday,
        start: monday,
    };
    const [interval, setInterval] = React.useState(persistedInterval ?? intervalForCurrentWeek);
    const lessonsStatus = useLessonsQueryApi({
        from: interval.start,
        to: interval.end,
    });
    /*
     * For Untis users: merge consecutive periods sharing the same `untisLessonId`
     * into one synthetic block BEFORE slot conversion. The existing
     * `convertLessonsToSlots` already derives `lengthHours` from the merged
     * duration, so a 3-hour English block becomes one cell spanning 3 slots.
     * Non-Untis users are 100% unaffected — `mergeLessonsIntoBlocks` short-circuits
     * when called for them, but we gate it for clarity and to save the work.
     */
    const isUntisUser = useIsUntisUser();
    const lessons = isUntisUser
        ? mergeLessonsIntoBlocks(lessonsStatus.data)
        : lessonsStatus.data;
    const lessonData = lessons.length
        ? convertLessonsToSlots(lessons)
        : [];
    const freeDaysStatus = useSchoolFreeDaysQueryApi(interval.start, interval.end);
    /*
     * Auto-refetch lessons after a fresh Untis import.
     * When timetableExists becomes true, the timetable structure exists but
     * lessons may still be in the process of being created by the backend.
     * This polls the Lessons query until data appears or timeout is reached.
     */
    const autoRefreshStartRef = useRef(null);
    useEffect(() => {
        if (!autoRefresh || lessonsStatus.data.length > 0) {
            return undefined;
        }
        if (!autoRefreshStartRef.current) {
            autoRefreshStartRef.current = Date.now();
        }
        const timer = window.setInterval(() => {
            if (autoRefreshStartRef.current &&
                Date.now() - autoRefreshStartRef.current > AUTO_REFRESH_TIMEOUT_MS) {
                window.clearInterval(timer);
                return;
            }
            void lessonsStatus.refetch();
        }, AUTO_REFRESH_INTERVAL_MS);
        return () => {
            window.clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run when lesson count changes
    }, [autoRefresh, lessonsStatus.data.length]);
    const displayedDateRange = `${format(interval.start, 'dd')}. - ${format(interval.end, 'dd. LLLL y')}`;
    const setWeekToCurrentWeek = () => {
        persistedInterval = intervalForCurrentWeek;
        setInterval(intervalForCurrentWeek);
    };
    const handleChangeWeek = (deltaWeeks) => {
        const newMonday = addWeeks(interval.start, deltaWeeks);
        const nextInterval = {
            end: endOfDay(nextFriday(newMonday)),
            start: newMonday,
        };
        persistedInterval = nextInterval;
        setInterval(nextInterval);
    };
    return (_jsxs(Stack, { spacing: 2, children: [_jsxs(Stack, { direction: "row", sx: {
                    alignItems: 'center',
                }, children: [_jsxs(Box, { sx: {
                            alignItems: 'center',
                            display: 'flex',
                            flexGrow: 1,
                            justifyContent: 'center',
                        }, children: [_jsx(IconButton, { "aria-label": t('previousWeekButton'), color: "secondary", onClick: () => {
                                    handleChangeWeek(-1);
                                }, sx: { px: 6, py: 2 }, children: _jsx(DoubleArrow, { sx: { rotate: '180deg' } }) }), _jsx(Typography, { component: "span", fontSize: "1.1rem", fontWeight: "bold", sx: { minWidth: '20ch', textAlign: 'center' }, "data-testid": "dateRange", children: displayedDateRange }), _jsx(IconButton, { "aria-label": t('nextWeekButton'), onClick: () => {
                                    handleChangeWeek(1);
                                }, color: "secondary", sx: { px: 6, py: 2 }, children: _jsx(DoubleArrow, {}) })] }), _jsx(Button, { color: "secondary", variant: "outlined", sx: { height: '2rem' }, disabled: isEqual(interval.start, intervalForCurrentWeek.start), onClick: setWeekToCurrentWeek, children: t('todayButton') })] }), _jsx(LoadingWrapper, { status: [lessonsStatus, freeDaysStatus], children: _jsx(WeeklySchedule, { lessonData: lessonData, dateOfMonday: interval.start, freeDaysItems: freeDaysStatus.items, freeDaysRowCount: freeDaysStatus.rowCount }) })] }));
};
export default CalendarForDashboard;
